import React, { useState, useEffect } from "react";
import { Container } from "reactstrap";

const DEFAULT_MESSAGES = [
  "Analyzing learning objectives...",
  "Designing engaging activities...",
  "Structuring lesson flow...",
  "Adding assessment strategies...",
  "Crafting your lesson plan...",
];

const shuffleArray = (items) => {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const LessonPlanLoader = ({ title = "Generating your lesson plan...", messages = DEFAULT_MESSAGES }) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [loadingImageIndex, setLoadingImageIndex] = useState(0);
  const [shouldSpin, setShouldSpin] = useState(false);
  const animationConfig = {
    durationSeconds: 8.4,
    containerHeight: 240,
    childHeight: 208,
    homeworkHeight: 169,
    dogHeight: 69,
    travelScale: 1.6,
  };
  const middleImageHeight = animationConfig.homeworkHeight / 3;
  const pizzaLoadingImage = require("../app/assets/img/alternative-tl-logos/pizza_loading_transparent.png");
  const homeworkLoadingImage = require("../app/assets/img/alternative-tl-logos/loading_homework_transparent.png");
  const crumpledPaperImage = require("../app/assets/img/crumpled_paper.png");
  const brokenPencilImage = require("../app/assets/img/broken-pencil.png");
  const activeMessages =
    Array.isArray(messages) && messages.length > 0 ? messages : DEFAULT_MESSAGES;

  const [loadingImages, setLoadingImages] = useState(() =>
    shuffleArray([
      pizzaLoadingImage,
      homeworkLoadingImage,
      crumpledPaperImage,
      brokenPencilImage,
    ])
  );
  const currentMiddleImage = loadingImages[loadingImageIndex];
  const isHomeworkImage = currentMiddleImage === homeworkLoadingImage;
  const isCrumpledPaperImage = currentMiddleImage === crumpledPaperImage;
  const isBrokenPencilImage = currentMiddleImage === brokenPencilImage;
  const currentMiddleImageHeight = isHomeworkImage
    ? middleImageHeight * 1.6
    : isBrokenPencilImage
      ? middleImageHeight * 0.6
      : middleImageHeight;
  const middleImageAnimation = isCrumpledPaperImage || isBrokenPencilImage || shouldSpin
    ? "spinContinuous 2.4s linear infinite"
    : "rock 1.6s ease-in-out infinite";
  const middleImageFilter = isBrokenPencilImage ? "grayscale(1)" : "none";
  useEffect(() => {
    if (!activeMessages.length) {
      return undefined;
    }
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % activeMessages.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [activeMessages.length]);

  useEffect(() => {
    const rotationDurationMs = animationConfig.durationSeconds * 1000;
    const interval = setInterval(() => {
      setLoadingImageIndex((prev) => {
        const nextIndex = prev + 1;
        if (nextIndex >= loadingImages.length) {
          setLoadingImages((current) => shuffleArray(current));
          return 0;
        }
        return nextIndex;
      });
    }, rotationDurationMs);

    return () => clearInterval(interval);
  }, [animationConfig.durationSeconds, loadingImages.length]);

  useEffect(() => {
    setShouldSpin((prev) => !prev);
  }, [loadingImageIndex]);

  return (
    <Container style={{ maxWidth: "900px" }}>
      <div className="lesson-plan-loader" style={{ padding: "4rem 2rem", textAlign: "center" }}>
        {/* Loading scene animation */}
        <div
          className="loader-animation"
          style={{
            position: "relative",
            height: `${animationConfig.containerHeight}px`,
            marginBottom: "2rem",
            overflow: "hidden",
          }}
        >
          <div
            className="loading-row"
            style={{
              position: "absolute",
              left: "0",
              right: "0",
              top: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "2.5rem",
              padding: "0 4%",
              animation: `slideLeft ${animationConfig.durationSeconds}s linear infinite`,
            }}
          >
            <img
              src={require("../app/assets/img/alternative-tl-logos/loading_child_transparent.png")}
              alt="Loading child"
              style={{
                height: `${animationConfig.childHeight}px`,
                width: "auto",
                animation: "floatY 2.2s ease-in-out infinite",
              }}
            />
            <img
              src={currentMiddleImage}
              alt="Loading papers"
              style={{
                height: `${currentMiddleImageHeight}px`,
                width: "auto",
                animation: middleImageAnimation,
                transformOrigin: "50% 60%",
                filter: middleImageFilter,
              }}
            />
            <img
              src={require("../app/assets/img/alternative-tl-logos/jack_russell_terrier_transparent_bg.png")}
              alt="Loading dog"
              style={{
                height: `${animationConfig.dogHeight}px`,
                width: "auto",
                animation: "floatY 2.2s ease-in-out infinite",
              }}
            />
          </div>
        </div>

        {/* Loading Text */}
        <h4
          style={{
            color: "var(--color-primary)",
            fontWeight: "600",
            fontSize: "1.5rem",
            marginBottom: "1rem",
            animation: "fadeIn 0.5s ease-in",
          }}
        >
          {title}
        </h4>

        {/* Cycling Messages */}
        <p
          key={messageIndex}
          style={{
            color: "#666",
            fontSize: "1.1rem",
            marginBottom: "2rem",
            minHeight: "30px",
            animation: "slideIn 0.5s ease-out",
          }}
        >
          {activeMessages[messageIndex]}
        </p>

        {/* Progress Bar */}
        <div
          style={{
            maxWidth: "400px",
            margin: "0 auto",
            height: "6px",
            background: "#e0e0e0",
            borderRadius: "3px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              background: "linear-gradient(90deg, var(--color-primary), var(--color-primary-light), var(--color-primary))",
              backgroundSize: "200% 100%",
              borderRadius: "3px",
              animation: "shimmer 2s linear infinite",
            }}
          />
        </div>

        {/* Additional Info */}
        <p
          style={{
            color: "#999",
            fontSize: "0.9rem",
            marginTop: "2rem",
            fontStyle: "italic",
          }}
        >
          This may take a moment. Your personalized content is being created.
        </p>

        {/* CSS Animations */}
        <style>{`
          @keyframes slideLeft {
            0% {
              transform: translate(120%, -50%) scale(${animationConfig.travelScale});
              opacity: 1;
            }
            100% {
              transform: translate(-120%, -50%) scale(${animationConfig.travelScale});
              opacity: 1;
            }
          }

          @keyframes shimmer {
            0% {
              background-position: 200% 0;
            }
            100% {
              background-position: -200% 0;
            }
          }

          @keyframes rock {
            0% {
              transform: rotate(-6deg);
            }
            50% {
              transform: rotate(6deg);
            }
            100% {
              transform: rotate(-6deg);
            }
          }

          @keyframes spinContinuous {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }

          @keyframes floatY {
            0% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-8px);
            }
            100% {
              transform: translateY(0);
            }
          }

          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </div>
    </Container>
  );
};

export default LessonPlanLoader;
